import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { getAuth } from "firebase/auth";
import api from "../../../../hooks/api";
import { useAxios } from "../../../../hooks/axios";
import useUser from "../../../../hooks/user";
import { useLogEvent } from "../../../../hooks/log";
import { Setting } from "./Settings";
import ConfirmActionDialog from "../../../common/dialogs/ConfirmActionDialog";
import Button from "../../../common/Button";
import classNames from "./DeleteAccount.module.css";

export default function DeleteAccount() {
  const axios = useAxios();
  const deleteAccount = api.useDeleteAccount(axios);
  const [, setUser] = useUser();
  const navigate = useNavigate();
  const logEvent = useLogEvent();

  const [isOpen, setIsOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = useCallback(async () => {
    setIsDeleting(true);
    try {
      await deleteAccount();

      // Sign out and clear the stored token *before* anything else touches
      // the API. The gateway's auth middleware re-creates the user on any
      // authenticated request (even /v1/log), so a stray call made with the
      // still-valid token here would resurrect the account we just deleted.
      try {
        await getAuth().signOut();
      } catch (error) {
        // Ignore errors
        console.warn(error);
      }

      localStorage.clear();
      setUser((prevState) => ({
        ...prevState,
        auth: undefined,
      }));
      navigate("/");
      logEvent({ type: "delete_account" });
    } catch (error) {
      if (error instanceof AxiosError && error.response?.status === 409) {
        toast(
          <div className="Toast ErrorToast">
            {
              "You own an organization with other members. Remove them or transfer ownership before deleting your account."
            }
          </div>,
          { type: "error" },
        );
      } else {
        toast(
          <div className="Toast ErrorToast">
            {"Something went wrong, please try again or contact support"}
          </div>,
          { type: "error" },
        );
        throw error;
      }
    } finally {
      setIsDeleting(false);
      setIsOpen(false);
    }
  }, [deleteAccount, logEvent, setUser, navigate]);

  return (
    <Setting
      title="Delete account"
      subtitle="Permanently delete your account and all associated data"
    >
      <div className={classNames.DeleteAccount}>
        <p>
          This action is permanent. It deletes your account, your meetings and
          events, and your recordings. You cannot undo this action.
        </p>
        <Button
          color="white"
          backgroundColor="red"
          borderColor="red"
          onClick={() => setIsOpen(true)}
        >
          Delete account
        </Button>
      </div>
      <ConfirmActionDialog
        title="Delete account"
        open={isOpen}
        onOpenChange={setIsOpen}
        onConfirm={handleDelete}
        action="Delete account"
        disabled={isDeleting}
        content={
          <h3>
            Are you sure you want to permanently delete your account? This
            cannot be undone.
          </h3>
        }
      />
    </Setting>
  );
}
